import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  message,
} from "antd";
import { CalendarOutlined, PlusOutlined } from "@ant-design/icons";
import api from "../../api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface UlEventRow {
  id: number | string;
  name: string;
  dates: string;       // formatted string for table
  cost: number | null; // we'll use actual_cost
  revenue: number | null; // actual_revenue
}

const EventsPage: React.FC = () => {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [data, setData] = useState<UlEventRow[]>([]);
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      title: "Event Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Event Dates",
      dataIndex: "dates",
      key: "dates",
    },
    {
      title: "Event Cost",
      dataIndex: "cost",
      key: "cost",
      render: (value: number | null) =>
        value != null ? value.toLocaleString() : "-",
    },
    {
      title: "Event Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number | null) =>
        value != null ? value.toLocaleString() : "-",
    },
  ];

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ul/events/");
      const list: any[] = res.data;
      setData(
        list.map((ev) => {
          const start = ev.start_datetime;
          const end = ev.end_datetime;
          const dateLabel =
            start && end ? `${start.slice(0, 10)} ~ ${end.slice(0, 10)}` : "TBD";

          // Prefer actual_cost/actual_revenue, fallback to estimated
          const cost =
            ev.actual_cost != null
              ? Number(ev.actual_cost)
              : ev.estimated_cost != null
              ? Number(ev.estimated_cost)
              : null;

          const revenue =
            ev.actual_revenue != null
              ? Number(ev.actual_revenue)
              : ev.estimated_revenue != null
              ? Number(ev.estimated_revenue)
              : null;

          return {
            id: ev.id,
            name: ev.name,
            dates: dateLabel,
            cost,
            revenue,
          } as UlEventRow;
        })
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    const [start, end] = values.dates || [];
    if (!start || !end) {
      message.error("Please select event dates.");
      return;
    }

    const payload = {
      name: values.name,
      start_datetime: start.toISOString(),
      end_datetime: end.toISOString(),
      actual_cost: values.cost ?? null,
      actual_revenue: values.revenue ?? null,
    };

    try {
      await api.post("/ul/events/", payload);
      message.success("Event saved.");
      await loadEvents();
    } catch (err) {
      console.error(err);
      message.error("Failed to save event.");
    } finally {
      handleCloseModal();
    }
  };

  return (
    <div style={{ padding: "40px", background: "#f0f2f5", minHeight: "100vh" }}>
      {/* Header card */}
      <Card
        bordered={false}
        style={{
          maxWidth: 900,
          margin: "0 auto 40px auto",
          textAlign: "center",
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <CalendarOutlined
          style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }}
        />
        <Title level={2} style={{ marginBottom: 8 }}>
          Ultra Lounge Events
        </Title>
        <Text type="secondary">
          Track event-level costs, revenue, and performance. Click an event row
          to view its detailed P&amp;L breakdown.
        </Text>
      </Card>

      {/* Events table */}
      <Row justify="center">
        <Col xs={24} sm={22} md={20} lg={18}>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ marginBottom: 0 }}>
                Event List
              </Title>

              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
                Add Event
              </Button>
            </div>

            <Text type="secondary">
              This table lists all Ultra Lounge events. Each row links to an Event
              Details Page, where you can record and analyze event-specific P&amp;L.
            </Text>

            <div style={{ marginTop: 24 }}>
              <Table<UlEventRow>
                rowKey="id"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={false}
                locale={{
                  emptyText: "No events yet — add your first Ultra Lounge event here.",
                }}
                onRow={(record) => ({
                  onClick: () => {
                    navigate(`/ul/events/${record.id}`);
                  },
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Add Event Modal */}
      <Modal
        title="Add Event"
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={() => form.submit()}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Event Name"
            name="name"
            rules={[{ required: true, message: "Please enter the event name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Event Dates"
            name="dates"
            rules={[{ required: true, message: "Please select the event dates" }]}
          >
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Event Cost (₩)" name="cost">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Event Revenue (₩)" name="revenue">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EventsPage;
